<template>
  <div class="home">
    <!-- ===== HERO ===== -->
    <section class="hero">
      <!-- Aurora background orbs -->
      <div class="aurora" aria-hidden="true">
        <div class="orb orb-1"></div>
        <div class="orb orb-2"></div>
        <div class="orb orb-3"></div>
      </div>
      <!-- Dot grid -->
      <div class="dot-grid" aria-hidden="true"></div>

      <div class="container hero-grid">
        <!-- Left: text -->
        <div class="hero-left">
          <div class="location-pill">
            <span class="live-dot"></span>
            Chennai, India
          </div>

          <h1 class="hero-name">
            Hi, I'm
            <span class="name-gradient">Vidya</span>
          </h1>

          <div class="hero-roles">
            <span>SDE at AlphaBake</span>
            <span class="sep">·</span>
            <span>AI Fashion Tech</span>
            <span class="sep">·</span>
            <span>Explorer</span>
          </div>

          <p class="hero-desc">
            Developer with 6+ years building visual AI tools for fashion e-commerce.
            Core member of AlphaBake — virtual try-on systems, generative AI workflows,
            and cloud infrastructure at scale.
          </p>

          <div class="hero-cta">
            <RouterLink to="/projects" class="btn btn-primary">
              View My Work
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </RouterLink>
            <RouterLink to="/about" class="btn btn-secondary">About Me</RouterLink>
          </div>
        </div>

        <!-- Right: profile visual -->
        <div class="hero-right">
          <div class="profile-card">
            <div class="card-glow" aria-hidden="true"></div>

            <!-- Gradient border wrapper -->
            <div class="photo-border">
              <!-- Corner accents -->
              <span class="corner c-tl" aria-hidden="true"></span>
              <span class="corner c-tr" aria-hidden="true"></span>
              <span class="corner c-bl" aria-hidden="true"></span>
              <span class="corner c-br" aria-hidden="true"></span>

              <div class="photo-inner">
                <img src="/images/unnamed Background Removed2.png" alt="Vidya Siva" />
              </div>
            </div>

            <!-- Nameplate below photo -->
            <div class="nameplate">
              <div class="np-info">
                <div class="np-name">Vidya Siva</div>
                <div class="np-role">SDE · AlphaBake</div>
              </div>
              <div class="np-status">
                <div class="np-dot"></div>
                <span>Open to work</span>
              </div>
            </div>

            <!-- Badges -->
            <div class="badge-row">
              <span class="tbadge">Vue.js</span>
              <span class="tbadge">Python</span>
              <span class="tbadge">ComfyUI</span>
              <span class="tbadge">AWS</span>
              <span class="tbadge">GCP</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats bar -->
      <div class="container">
        <div class="stats-bar">
          <div class="stat">
            <div class="stat-num gradient-text">6+</div>
            <div class="stat-label">Years of Experience</div>
          </div>
          <div class="stat-rule"></div>
          <div class="stat">
            <div class="stat-num gradient-text">3–5k</div>
            <div class="stat-label">Virtual Try-ons / Day</div>
          </div>
          <div class="stat-rule"></div>
          <div class="stat">
            <div class="stat-num gradient-text">5M+</div>
            <div class="stat-label">Images Generated</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== WHAT I BUILD ===== -->
    <section class="skills-section">
      <div class="container">
        <div class="sec-header reveal">
          <div class="section-tag">Capabilities</div>
          <h2 class="sec-title">What I Build</h2>
        </div>

        <div class="skills-grid">
          <div class="skill-card reveal reveal-delay-1">
            <div class="s-icon blue">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <h3>AI & Generative Tools</h3>
            <p>ComfyUI pipelines, LoRA training, and AI model fine-tuning for personalized fashion content generation at scale.</p>
          </div>

          <div class="skill-card reveal reveal-delay-2">
            <div class="s-icon purple">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <h3>Virtual Try-On</h3>
            <p>Advanced computer vision systems for digital garment visualization — used by thousands of shoppers daily.</p>
          </div>

          <div class="skill-card reveal reveal-delay-3">
            <div class="s-icon gold">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <path d="M14 17.5h7M17.5 14v7"/>
              </svg>
            </div>
            <h3>Full Stack Development</h3>
            <p>End-to-end apps with Vue.js, Python, Django, and Node.js — built for scale in production fashion tech.</p>
          </div>

          <div class="skill-card reveal reveal-delay-4">
            <div class="s-icon green">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
              </svg>
            </div>
            <h3>Cloud & Infrastructure</h3>
            <p>AWS, GCP, Azure management with Docker, GPU optimisation, and automated cloud utilities.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== PROJECTS CTA ===== -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-box reveal">
          <div class="cta-glow" aria-hidden="true"></div>

          <div class="cta-text">
            <div class="section-tag">Featured Work</div>
            <h2>AI-Driven<br>Fashion Tech</h2>
            <p>From virtual try-on to generative catalogs — explore the projects that push the limits of AI in fashion e-commerce.</p>
            <RouterLink to="/projects" class="btn btn-primary">
              Explore Projects
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </RouterLink>
          </div>

          <div class="cta-cards">
            <div class="cta-card">
              <div class="cta-dot blue"></div>
              <div class="cta-card-info">
                <span>Virtual Try-On</span>
                <em>ComfyUI · Django · Vertex AI</em>
              </div>
            </div>
            <div class="cta-card">
              <div class="cta-dot purple"></div>
              <div class="cta-card-info">
                <span>Generative AI Pipelines</span>
                <em>LoRA · Fine-tuning · ComfyUI</em>
              </div>
            </div>
            <div class="cta-card">
              <div class="cta-dot gold"></div>
              <div class="cta-card-info">
                <span>Fab3D Fabric Tool</span>
                <em>Blender · Python · 3D Graphics</em>
              </div>
            </div>
            <div class="cta-card">
              <div class="cta-dot green"></div>
              <div class="cta-card-info">
                <span>Model & Background Swap</span>
                <em>CV · Generative AI · Automation</em>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible') }),
    { threshold: 0.12 }
  )
  document.querySelectorAll('.reveal').forEach(el => observer!.observe(el))
})

onUnmounted(() => {
  observer?.disconnect()
})
</script>

<style scoped>
/* ===== PAGE ===== */
.home {
  padding-top: 80px;
}

/* ===== HERO ===== */
.hero {
  position: relative;
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: var(--space-20) 0 var(--space-12);
  overflow: hidden;
}

/* Aurora orbs */
.aurora {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(110px);
  opacity: 0.13;
}

.orb-1 {
  width: 700px;
  height: 700px;
  background: var(--primary);
  top: -280px;
  right: -120px;
  animation: drift1 13s ease-in-out infinite;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: var(--purple);
  bottom: -180px;
  left: -80px;
  animation: drift2 11s ease-in-out infinite;
}

.orb-3 {
  width: 360px;
  height: 360px;
  background: var(--gold);
  top: 35%;
  left: 38%;
  opacity: 0.07;
  animation: drift3 15s ease-in-out infinite;
}

@keyframes drift1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-30px, 30px); }
}
@keyframes drift2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(24px, -20px); }
}
@keyframes drift3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-18px, -28px) scale(1.08); }
}

/* Dot grid */
.dot-grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
}

[data-theme="light"] .dot-grid {
  background-image: radial-gradient(circle, rgba(0, 0, 0, 0.055) 1px, transparent 1px);
}

/* Hero layout */
.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
  position: relative;
  z-index: 1;
  min-height: 520px;
  margin-bottom: var(--space-12);
}

/* Left */
.hero-left {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.location-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-full);
  font-size: 0.8rem;
  color: var(--text-muted);
  font-family: var(--font-display);
  font-weight: 500;
  width: fit-content;
}

[data-theme="light"] .location-pill {
  background: rgba(0, 0, 0, 0.04);
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: live-pulse 2.2s infinite;
}

@keyframes live-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
  60% { opacity: 0.9; box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
}

.hero-name {
  font-size: clamp(2.6rem, 5.5vw, 4.8rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.025em;
}

.name-gradient {
  display: block;
  background: linear-gradient(130deg, var(--primary), var(--purple));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-roles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-family: var(--font-display);
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-muted);
}

.sep { color: var(--text-dim); }

.hero-desc {
  font-size: 1rem;
  color: var(--text-muted);
  line-height: 1.72;
  max-width: 470px;
}

.hero-cta {
  display: flex;
  gap: var(--space-4);
  flex-wrap: wrap;
}

/* Right — profile */
.hero-right {
  display: flex;
  justify-content: center;
  align-items: center;
}

.profile-card {
  position: relative;
  width: 290px;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  transform: rotate(-3deg);
  transition: transform 0.5s var(--ease-spring);
  animation: card-float 7s ease-in-out infinite;
}

.profile-card:hover {
  transform: rotate(0deg) scale(1.02);
  animation-play-state: paused;
}

@keyframes card-float {
  0%, 100% { transform: translateY(0px) rotate(-3deg); }
  50%  { transform: translateY(-12px) rotate(-3deg); }
}

/* Ambient glow */
.card-glow {
  position: absolute;
  inset: -40px;
  border-radius: 50%;
  background: radial-gradient(ellipse at center,
    rgba(91, 143, 255, 0.2) 0%,
    rgba(167, 139, 250, 0.1) 40%,
    transparent 70%);
  pointer-events: none;
  filter: blur(20px);
  z-index: 0;
}

/* Gradient border wrap — outer is the gradient, inner clips to solid */
.photo-border {
  position: relative;
  z-index: 1;
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 20px;
  padding: 2px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--purple) 50%, var(--gold) 100%);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04),
    0 30px 70px rgba(0, 0, 0, 0.65),
    0 0 50px rgba(91, 143, 255, 0.15);
}

.photo-inner {
  width: 100%;
  height: 100%;
  border-radius: 18px;
  overflow: hidden;
  background: var(--surface-2);
}

.photo-inner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

/* Corner bracket accents */
.corner {
  position: absolute;
  width: 18px;
  height: 18px;
  z-index: 3;
  pointer-events: none;
}

.corner::before,
.corner::after {
  content: '';
  position: absolute;
  background: #fff;
  border-radius: 2px;
  opacity: 0.7;
}

.corner::before { width: 2px; height: 100%; }
.corner::after  { width: 100%; height: 2px; }

.c-tl { top: 6px;    left: 6px;   }
.c-tr { top: 6px;    right: 6px;  transform: scaleX(-1); }
.c-bl { bottom: 6px; left: 6px;   transform: scaleY(-1); }
.c-br { bottom: 6px; right: 6px;  transform: scale(-1); }

/* Nameplate below photo */
.nameplate {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--surface-2);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
}

.np-name {
  font-family: var(--font-display);
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 3px;
}

.np-role {
  font-size: 0.76rem;
  color: var(--text-muted);
  font-weight: 500;
}

.np-status {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.np-status span {
  font-size: 0.7rem;
  color: #22c55e;
  font-weight: 600;
  font-family: var(--font-display);
  white-space: nowrap;
}

.np-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: live-pulse 2.2s infinite;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
}

/* Tech badges row */
.badge-row {
  position: relative;
  z-index: 1;
  display: flex;
  gap: var(--space-2);
  justify-content: center;
  flex-wrap: nowrap;
  overflow: hidden;
}

.tbadge {
  padding: 5px 11px;
  background: var(--surface-2);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-full);
  font-family: var(--font-display);
  font-size: 0.73rem;
  font-weight: 600;
  color: var(--text-muted);
  transition: all var(--transition-fast);
  cursor: default;
  white-space: nowrap;
}

.tbadge:hover {
  color: var(--primary);
  border-color: rgba(91, 143, 255, 0.4);
  background: var(--primary-dim);
}

/* Stats bar */
.stats-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-12);
  padding: var(--space-8) var(--space-12);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  position: relative;
  z-index: 1;
}

[data-theme="light"] .stats-bar {
  background: rgba(255, 255, 255, 0.65);
}

.stat { text-align: center; }

.stat-num {
  font-family: var(--font-display);
  font-size: 2.1rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-rule {
  width: 1px;
  height: 48px;
  background: var(--border-light);
}

/* ===== SKILLS ===== */
.skills-section {
  padding: var(--space-24) 0;
}

.sec-header {
  text-align: center;
  margin-bottom: var(--space-12);
}

.sec-title {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
}

.skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--space-6);
}

.skill-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-8);
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
}

.skill-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity var(--transition-normal);
  border-radius: inherit;
  pointer-events: none;
}

.skill-card:nth-child(1)::before {
  background: radial-gradient(circle at 0% 0%, rgba(91, 143, 255, 0.1), transparent 55%);
}
.skill-card:nth-child(2)::before {
  background: radial-gradient(circle at 0% 0%, rgba(167, 139, 250, 0.1), transparent 55%);
}
.skill-card:nth-child(3)::before {
  background: radial-gradient(circle at 0% 0%, rgba(232, 184, 75, 0.1), transparent 55%);
}
.skill-card:nth-child(4)::before {
  background: radial-gradient(circle at 0% 0%, rgba(34, 197, 94, 0.1), transparent 55%);
}

.skill-card:hover {
  border-color: var(--border-light);
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.skill-card:hover::before {
  opacity: 1;
}

.s-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-5);
  position: relative;
  z-index: 1;
}

.s-icon.blue   { background: var(--primary-dim); border: 1px solid rgba(91,143,255,0.2); color: var(--primary); }
.s-icon.purple { background: var(--purple-dim);  border: 1px solid rgba(167,139,250,0.2); color: var(--purple); }
.s-icon.gold   { background: var(--gold-dim);    border: 1px solid rgba(232,184,75,0.2); color: var(--gold); }
.s-icon.green  { background: var(--green-dim);   border: 1px solid rgba(34,197,94,0.2);  color: var(--green); }

.skill-card h3 {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: var(--space-3);
  position: relative;
  z-index: 1;
}

.skill-card p {
  font-size: 0.88rem;
  line-height: 1.65;
  position: relative;
  z-index: 1;
}

/* ===== CTA SECTION ===== */
.cta-section {
  padding: var(--space-24) 0 var(--space-16);
}

.cta-box {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  padding: var(--space-16);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
  position: relative;
  overflow: hidden;
}

.cta-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: radial-gradient(ellipse at 0% 50%, rgba(91, 143, 255, 0.08), transparent 60%);
  pointer-events: none;
}

.cta-text {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  position: relative;
  z-index: 1;
}

.cta-text h2 {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.cta-text p {
  font-size: 0.95rem;
  line-height: 1.72;
  max-width: 400px;
}

.cta-cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  position: relative;
  z-index: 1;
}

.cta-card {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
  cursor: default;
}

[data-theme="light"] .cta-card {
  background: rgba(255, 255, 255, 0.8);
}

.cta-card:hover {
  border-color: var(--primary);
  transform: translateX(5px);
}

.cta-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cta-dot.blue   { background: var(--primary); }
.cta-dot.purple { background: var(--purple); }
.cta-dot.gold   { background: var(--gold); }
.cta-dot.green  { background: var(--green); }

.cta-card-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cta-card-info span {
  font-family: var(--font-display);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text);
}

.cta-card-info em {
  font-style: normal;
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .home { padding-top: 70px; }

  .hero {
    padding: var(--space-12) 0 var(--space-8);
    min-height: auto;
  }

  .hero-grid {
    grid-template-columns: 1fr;
    text-align: center;
    gap: var(--space-10);
    min-height: auto;
  }

  .hero-right { order: -1; }

  .location-pill, .hero-cta { margin: 0 auto; }

  .hero-desc { max-width: 100%; }

  .hero-cta { justify-content: center; }

  .profile-card {
    width: 220px;
    margin: 0 auto;
    transform: rotate(0deg);
    animation: none;
  }

  @keyframes card-float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-8px) rotate(0deg); }
  }

  .stats-bar {
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-6);
  }

  .stat-rule {
    width: 48px;
    height: 1px;
  }

  .skills-grid { grid-template-columns: 1fr; }

  .cta-box {
    grid-template-columns: 1fr;
    padding: var(--space-10) var(--space-8);
    gap: var(--space-8);
  }
}
</style>
